import { gql } from '@apollo/client'

import { PromotionDistribution } from '../types/cart'

export const PROMOTION_SUMMARY_FIELDS = gql`
  fragment PromotionSummaryFields on Promotion {
    __typename
    id
    name
    providerCode
    # Server-resolved and localised. Requested because the UI already prefers it —
    # PromotionRow renders \`providerName || providerCode\` — but neither fragment asked
    # for it, so the fallback fired on every row and the column showed raw codes. For a
    # provider outside the caller's own list that code is an opaque token, not a
    # readable identifier.
    providerName
    status
    discountType
    discountValue
    currency
    codeMode
    distribution
    canWrite
    updatedAt
  }
`

export const PROMOTION_FIELDS = gql`
  fragment PromotionFields on Promotion {
    __typename
    id
    name
    providerCode
    providerName
    status
    discountType
    discountValue
    currency
    scope {
      propertyCodes
      ratePlanCodes
      roomTypeCodes
      channelCodes
    }
    consumableVia
    schedule {
      includeRanges {
        start
        end
        reason
      }
      blackoutRanges {
        start
        end
        reason
      }
      bookingWindowRanges {
        start
        end
        reason
      }
      daysOfWeek
      recurringEventRefs
      advancePurchaseMinDays
      advancePurchaseMaxDays
    }
    caps {
      maxRedemptions
      maxDiscountBudgetMinor
      budgetCurrency
      maxRedemptionsPerGuest
    }
    codeMode
    distribution
    advertisementOptIn
    code
    stackable
    priority
    decisionTableRef
    canWrite
    createdAt
    updatedAt
  }
`

export const PROMOTIONS_QUERY = gql`
  query Promotions(
    $providerCode: String
    $status: PromotionStatus
    $page: Int
    $perPage: Int
  ) {
    promotions(
      providerCode: $providerCode
      status: $status
      page: $page
      perPage: $perPage
    ) {
      promotions {
        ...PromotionSummaryFields
      }
      total
    }
  }
  ${PROMOTION_SUMMARY_FIELDS}
`

export const PROMOTION_QUERY = gql`
  query Promotion($id: ID!) {
    promotion(id: $id) {
      ...PromotionFields
    }
  }
  ${PROMOTION_FIELDS}
`

export const SAVE_PROMOTION_MUTATION = gql`
  mutation SavePromotion($input: PromotionInput!, $publish: Boolean!) {
    savePromotion(input: $input, publish: $publish) {
      ...PromotionFields
    }
  }
  ${PROMOTION_FIELDS}
`

export const DELETE_PROMOTION_MUTATION = gql`
  mutation DeletePromotion($id: ID!, $updatedAt: String!) {
    deletePromotion(id: $id, updatedAt: $updatedAt) {
      message
    }
  }
`

export const UPLOAD_VOUCHERS_MUTATION = gql`
  mutation UploadVouchers($input: VoucherUploadInput!) {
    uploadVouchers(input: $input) {
      accepted
      duplicates
      invalid
      committed
    }
  }
`

export const PROMOTION_EVALUATION_TRACE_QUERY = gql`
  query PromotionEvaluationTrace($cartId: String, $bookingId: String) {
    promotionEvaluationTrace(cartId: $cartId, bookingId: $bookingId) {
      applied {
        promotionId
        name
        providerCode
        discountType
        discountMinor
        currency
        distribution
        priority
        stackable
        appliedLineIds
      }
      skipped {
        promotionId
        name
        reason
        reasonText
        # The service marks the entry the entered code named. Without it the client
        # guesses by reason and falls back to array position, which nothing orders.
        matchedSuppliedCode
      }
      evaluatedAt
      consumedVia
    }
  }
`

export interface PromotionEvaluationTrace {
  applied: Array<{
    promotionId: string
    name: string
    providerCode: string
    discountType: string
    discountMinor: string
    currency: string
    distribution: PromotionDistribution
    priority: number
    stackable: boolean
    appliedLineIds: string[]
  }>
  skipped: Array<{
    promotionId: string
    name: string
    reason: string
    reasonText: string
  }>
  evaluatedAt: string
  consumedVia: string
}

export interface PromotionDateRange {
  start: string
  end: string
  reason?: string | null
}

export interface Promotion {
  id: string
  name: string
  providerCode: string
  providerName?: string | null
  status: string
  discountType: string
  discountValue: string
  currency: string
  scope: {
    propertyCodes: string[]
    ratePlanCodes: string[]
    roomTypeCodes: string[]
    channelCodes: string[]
  }
  consumableVia: string[]
  schedule: {
    includeRanges: PromotionDateRange[]
    blackoutRanges: PromotionDateRange[]
    bookingWindowRanges: PromotionDateRange[]
    daysOfWeek: number[]
    recurringEventRefs: string[]
    advancePurchaseMinDays?: number | null
    advancePurchaseMaxDays?: number | null
  }
  caps: {
    maxRedemptions?: string | null
    maxDiscountBudgetMinor?: string | null
    budgetCurrency?: string | null
    maxRedemptionsPerGuest?: number | null
  }
  codeMode: string
  distribution: PromotionDistribution
  advertisementOptIn: boolean
  code?: string | null
  stackable: boolean
  priority: number
  decisionTableRef?: string | null
  canWrite: boolean
  createdAt: string
  updatedAt: string
}

export interface PromotionListData {
  promotions: { promotions: Promotion[]; total: number }
}

export interface VoucherUploadResult {
  accepted: number
  duplicates: number
  invalid: string[]
  committed: boolean
}

export interface PromotionVariables {
  providerCode?: string
  status?: string
  page?: number
  perPage?: number
}

export interface PromotionInput {
  id?: string
  updatedAt?: string
  name: string
  providerCode: string
  discountType: string
  discountValue: string
  currency: string
  scope?: {
    propertyCodes?: string[]
    ratePlanCodes?: string[]
    roomTypeCodes?: string[]
    channelCodes?: string[]
  }
  consumableVia?: string[]
  schedule?: {
    includeRanges?: PromotionDateRange[]
    blackoutRanges?: PromotionDateRange[]
    bookingWindowRanges?: PromotionDateRange[]
    daysOfWeek?: number[]
    recurringEventRefs?: string[]
    advancePurchaseMinDays?: number
    advancePurchaseMaxDays?: number
  }
  caps?: {
    maxRedemptions?: string
    maxDiscountBudgetMinor?: string
    budgetCurrency?: string
    maxRedemptionsPerGuest?: number
  }
  codeMode: string
  distribution: PromotionDistribution
  advertisementOptIn: boolean
  code?: string
  stackable: boolean
  priority: number
  decisionTableRef?: string
}

/**
 * A promotion's voucher codes and their lifecycle state.
 *
 * Reserve/Confirm/Release have always written these columns in loyalty; nothing read them
 * back, so a pool could be issued and never audited (BL-145).
 */
export const PROMOTION_CODES_QUERY = gql`
  query PromotionCodes(
    $promotionId: ID!
    $state: String
    $page: Int
    $perPage: Int
  ) {
    promotionCodes(
      promotionId: $promotionId
      state: $state
      page: $page
      perPage: $perPage
    ) {
      codes {
        code
        state
        bookingId
        consumedAt
        createdAt
      }
      total
      totalAvailable
      totalReserved
      totalConsumed
    }
  }
`

export interface PromotionCode {
  code: string
  state: string
  bookingId: string
  // Null until consumed. An available code has never been consumed, and the server sends
  // absent rather than a zero timestamp precisely so this stays distinguishable.
  consumedAt?: string | null
  createdAt: string
}

export interface PromotionCodesData {
  promotionCodes: {
    codes: PromotionCode[]
    total: number
    // Totals across the WHOLE pool — unaffected by the state filter or the page.
    totalAvailable: number
    totalReserved: number
    totalConsumed: number
  }
}

export interface PromotionCodesVariables {
  promotionId: string
  state?: string
  page?: number
  perPage?: number
}

/**
 * Campaign performance (AC-14).
 *
 * Sourced from the counter loyalty maintains transactionally on Confirm and Release — the
 * same row cap enforcement reads — so these figures cannot disagree with the caps set on
 * the promotion.
 */
export const PROMOTION_STATS_QUERY = gql`
  query PromotionStats($promotionId: ID!, $from: String, $to: String) {
    promotionStats(promotionId: $promotionId, from: $from, to: $to) {
      appliedCount
      discountMinor
      inFlightCount
      inFlightDiscountMinor
      maxRedemptions
      maxDiscountBudgetMinor
      currency
      # Echoed by the server, null for a lifetime response. Read rather than assumed from
      # the variables: it is what the server actually applied, and it is what tells the
      # panel whether "of cap" is a valid thing to render.
      periodFrom
      periodTo
    }
  }
`

export interface PromotionStatsData {
  promotionStats: {
    appliedCount: number
    // Money arrives as a STRING: int64 minor units exceed what a JSON number can
    // represent exactly, and a campaign budget in IRR is large enough to reach that.
    discountMinor: string
    inFlightCount: number
    inFlightDiscountMinor: string
    maxRedemptions: number
    maxDiscountBudgetMinor: string
    currency: string
    // The window the server applied, null for a lifetime response. Nullable rather than
    // an empty string so presence is a real check — the panel keys "is this scoped?" off
    // it, and "" would read as a window that exists.
    periodFrom: string | null
    periodTo: string | null
  }
}

/**
 * Offers the booker can use on THIS cart.
 *
 * Cart-scoped rather than a provider catalogue: the promotion service filters by
 * scope, schedule, currency and live caps against the cart's own lines, so
 * everything returned is usable right now. Conditions come back for display only —
 * eligibility is already decided server-side, and re-deriving it in the client
 * would drift the moment either side changed.
 */
/**
 * Ask whether a code works on this cart, WITHOUT booking anything.
 *
 * `skipped` is the point of it. A promotion that cannot apply is an ordinary outcome,
 * not an error, and `reasonText` is already translated FA/EN by the promotion service —
 * render it directly. Do NOT map `reason` to wording here: the service distinguishes
 * roughly eighteen reasons and any client-side table collapses them into a handful, which
 * is how a booker ends up told "not eligible for this stay" for a blackout date, a
 * day-of-week rule and an exhausted budget alike.
 *
 * `guest` is optional. It is the only way a per-guest cap can be ANSWERED rather than
 * deferred, and the phone is not collected until the next step — so the first call omits
 * it and a capped offer says it needs guest details.
 */
export const EVALUATE_PROMOTION_QUERY = gql`
  query EvaluatePromotion(
    $cartID: String!
    $intent: PromotionIntentInput!
    $guest: GuestIdentityInput
  ) {
    evaluatePromotion(cartID: $cartID, intent: $intent, guest: $guest) {
      totalDiscountMinor
      currency
      applied {
        promotionId
        name
        discountMinor
        currency
        distribution
        # roomStayReference becomes a booking identifier after reservation, so it cannot
        # reliably join the cart before Continue. The legacy line IDs are the stable cart
        # join keys, returned in allocation order by the promotion contract.
        appliedLineIds
        allocations {
          roomStayReference
          discountMinor
          currency
        }
      }
      skipped {
        promotionId
        name
        reason
        reasonText
        # Which refusal is the one the entered code named. Without it the client guesses
        # by reason prefix and then by array position, which nothing orders — so the
        # booker is shown an unrelated promotion's refusal as if it were their own.
        matchedSuppliedCode
      }
    }
  }
`

export const ADVERTISED_PROMOTIONS_QUERY = gql`
  query AdvertisedPromotions($cartID: String!) {
    advertisedPromotions(cartID: $cartID) {
      promotionId
      name
      code
      discountType
      discountValue
      currency
      stackable
      priority
      # The one thing in the advertised response a client must ACT on. Everything else
      # under conditions is a predicate the service already satisfied for this cart.
      requiresGuestIdentity
      conditions {
        stayPeriods {
          start
          end
        }
        bookingPeriods {
          start
          end
        }
        daysOfWeek
        advancePurchaseMinDays
        advancePurchaseMaxDays
        # Why an advertised offer may still not apply. ListAdvertisedPromotions
        # neutralises the per-guest cap so offers can be shown before the guest is
        # known; without this field the panel cannot tell the operator that.
        requiresGuestIdentity
      }
    }
  }
`

/**
 * The cross-promotion Overview.
 *
 * `minorUnitDigits` is selected on BOTH total shapes and on each row, and is nullable: the
 * server states the exponent where it knows the currency and says nothing where it does not.
 * Reading it is what lets the client stop keeping a currency table of its own — the table
 * that has already caused this defect nine times in this codebase.
 *
 * `periodFrom`/`periodTo` are read rather than inferred from the variables. They are what the
 * server ACTUALLY applied, and the applied figures change SOURCE with the window — unwindowed
 * from the usage counter that cap enforcement reads, windowed from an aggregate over
 * confirmed reservations. The two are not reconcilable by arithmetic, so the screen must be
 * able to say which it is showing rather than guess.
 */
export const PROMOTIONS_STATS_QUERY = gql`
  query PromotionsStats(
    $providerCode: String
    $from: String
    $to: String
    $page: Int
    $perPage: Int
    $orderBy: PromotionStatsOrder
    $descending: Boolean
    $status: PromotionStatus
  ) {
    promotionsStats(
      providerCode: $providerCode
      from: $from
      to: $to
      page: $page
      perPage: $perPage
      orderBy: $orderBy
      descending: $descending
      status: $status
    ) {
      page
      perPage
      total
      totalAppliedCount
      totalInFlightCount
      discountTotals {
        currency
        minorUnitDigits
        discountMinor
        appliedCount
      }
      inFlightDiscountTotals {
        currency
        minorUnitDigits
        discountMinor
        appliedCount
      }
      rows {
        promotionId
        name
        providerCode
        status
        currency
        minorUnitDigits
        appliedCount
        discountMinor
        inFlightCount
        inFlightDiscountMinor
      }
      periodFrom
      periodTo
    }
  }
`

/** One currency's money total. Never summed across currencies — see PromotionStatsList. */
export interface CurrencyTotal {
  currency: string
  /** Null when the server does not recognise the currency; 0 means it does and there is none. */
  minorUnitDigits: number | null
  /** Minor units, as a STRING: an int64 exceeds what a JSON number holds exactly. */
  discountMinor: string
  appliedCount: number
}

export interface PromotionStatsRow {
  promotionId: string
  name: string
  providerCode: string
  status: string
  currency: string
  minorUnitDigits: number | null
  appliedCount: number
  discountMinor: string
  inFlightCount: number
  inFlightDiscountMinor: string
}

export interface PromotionsStatsData {
  promotionsStats: {
    page: number
    perPage: number
    total: number
    totalAppliedCount: number
    totalInFlightCount: number
    discountTotals: CurrencyTotal[]
    inFlightDiscountTotals: CurrencyTotal[]
    rows: PromotionStatsRow[]
    periodFrom: string | null
    periodTo: string | null
  }
}

export const PROMOTION_SKIP_REASONS_QUERY = gql`
  query PromotionSkipReasons(
    $providerCode: String
    $from: String
    $to: String
    $page: Int
    $perPage: Int
  ) {
    promotionSkipReasons(
      providerCode: $providerCode
      from: $from
      to: $to
      page: $page
      perPage: $perPage
    ) {
      byReason {
        reason
        count
      }
      byPromotion {
        promotionId
        name
        reason
        count
      }
      page
      perPage
      total
      evaluationsExamined
      periodFrom
      periodTo
      retentionDays
    }
  }
`

export interface SkipReasonCount {
  reason: string
  count: number
}

/**
 * A promotion's skips, by reason.
 *
 * `promotionId` AND `name` ARE EMPTY ON THE UNATTRIBUTED BUCKET, and that is a real row, not
 * a malformed one. The service redacts before grouping: a skip belonging to a promotion the
 * caller has no standing to know is emitted with its identity blanked rather than dropped,
 * because omitting it would make this list's difference from `byReason` the exact number
 * withheld. Render it as an explicit unattributed row — never hide it, and never show a blank.
 */
export interface PromotionSkipCount {
  promotionId: string
  name: string
  reason: string
  count: number
}

export interface PromotionSkipReasonsData {
  promotionSkipReasons: {
    byReason: SkipReasonCount[]
    byPromotion: PromotionSkipCount[]
    page: number
    perPage: number
    total: number
    evaluationsExamined: number
    periodFrom: string | null
    periodTo: string | null
    retentionDays: number
  }
}
